<template>
    <v-form @submit.prevent="register" ref="form" class="register-form">
        <v-row>
            <v-col cols="12" sm="6" class="pb-1 pt-0">
                <v-text-field
                    outlined
                    placeholder="First name"
                    label="First Name"
                    name="first_name"
                    v-model="firstName"
                    :disabled="loading"
                    :rules="[rules.global.required]"
                />
            </v-col>
            <v-col cols="12" sm="6" class="pb-1 pt-0">
                <v-text-field
                    outlined
                    placeholder="Last name"
                    label="Last Name"
                    name="last_name"
                    v-model="lastName"
                    :disabled="loading"
                    :rules="[rules.global.required]"
                />
            </v-col>
            <v-col cols="12" class="pb-1 pt-0">
                <v-text-field
                    outlined
                    placeholder="you@example.com"
                    label="Email Address"
                    name="email"
                    v-model="email"
                    :disabled="loading || emailLocked"
                    :rules="[rules.global.required, ...rules.email]"
                    :error-messages="emailError"
                    :hint="emailLocked ? 'This address is set. Mail for this account goes to you.' : ''"
                    :persistent-hint="emailLocked"
                    @input="$emit('update:emailError', '')"
                />
                <div v-if="allowGenerateEmail && !emailLocked" class="mb-3">
                    <v-btn text small color="primary" style="text-transform: none" :loading="generatingEmail" :disabled="loading" @click="generateEmail">
                        <v-icon left small>mdi-at</v-icon>
                        No email? Create one for them
                    </v-btn>
                </div>
            </v-col>
            <v-col cols="12" class="pb-1 pt-0">
                <v-select
                    outlined
                    placeholder="How should we reach you?"
                    label="Preferred Contact Channel"
                    :items="['Email', 'Call', 'Whatsapp']"
                    v-model="preferredChannel"
                    :disabled="loading"
                    :rules="[rules.global.required]"
                />
            </v-col>
            <v-col cols="12" class="pb-1 pt-0">
                <vue-tel-input-vuetify
                    v-model="phone"
                    @validate="phoneValidated"
                    @country-changed="phoneCountryChanged"
                    outlined
                    class="phone-input ltr-dir"
                    :defaultCountry="defaultCountry"
                    :ignoredCountries="ignoredCountries"
                    placeholder="Phone number"
                    label="Phone Number"
                    :rules="[rules.global.required]"
                    :disabled="loading || disabled"
                />
            </v-col>
        </v-row>

        <v-btn
            type="submit"
            block
            depressed
            large
            :loading="loading"
            :disabled="loading"
            color="#D4780A"
            class="submit-btn white--text mt-1"
            @click.prevent="register"
        >
            {{ buttonText }}
        </v-btn>
    </v-form>
</template>

<script>

import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'

import AppAPI from '@/app-api'

export default {
    props: {
        loading: {type: Boolean, default: false,},
        buttonText: {type: String, default: 'Create Account'},
        dialogOpened: {type: Boolean, default: false,},
        emailError: {type: String, default: ''},
        flow: {type: String, default: 'evaluation'}, // 'evaluation' | 'consultation'
        // contact details to start from, e.g. a parent's own phone and channel
        initial: {type: Object, default: () => ({})},
        // offer to make up an address for someone who has no inbox
        allowGenerateEmail: {type: Boolean, default: false},
    },
    data(){
        return {
            firstName: '',
            lastName: '',
            email: '',
            preferredChannel: '',

            emailLocked: false,
            generatingEmail: false,

            phone: '',
            fullPhone: '',
            phoneValid: true,
            phoneCountry: '',

            disabled: false,

            ignoredCountries: ['IL'],  // will be deleted from select country of phone number input
            rules: {
                global: {
                    required: value => true
                },
                email: [
                    value => true
                ],
            },
            validationRules: {
                global: {
                    required: value => !!value || 'Input is required'
                },
                email: [
                    value => {
                        const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                        return pattern.test(value) || 'Email not valid';
                    }
                ],
            }
        }
    },
    computed: {
        defaultCountry(){
            return (this.initial && this.initial.phone_country) ? this.initial.phone_country.toUpperCase() : '';
        },
        ...GlobalComputed
    },
    mounted(){
        this.applyInitial();
    },
    methods: {
        applyInitial(){
            const initial = this.initial || {};

            if (initial.phone && !this.phone){
                this.phone = initial.phone;
            }
            if (initial.preferred_channel && !this.preferredChannel){
                this.preferredChannel = initial.preferred_channel;
            }
        },
        generateEmail(){
            if (this.generatingEmail) return;

            this.generatingEmail = true;
            this.$store.dispatch('generateChildEmail', {name: this.firstName}).then(email=>{
                this.generatingEmail = false;
                this.email = email;
                this.emailLocked = true;
                this.$emit('update:emailError', '');
            }).catch(()=>{
                this.generatingEmail = false;
                this.showMsg('Could not create an address, please type one');
            })
        },
        register(){
            this.rules = this.validationRules;
            window.setTimeout(()=>{
                if (this.formValidate()){
                    let data = {
                        name: this.firstName + ' ' + this.lastName,
                        email: this.email,
                        phone: this.phone,
                        full_phone: this.fullPhone,
                        phone_country: this.phoneCountry,
                        preferred_channel: this.preferredChannel,
                    };
                    const referral_token = this.$route.query.referral;
                    if (referral_token){
                        data.referral_token = referral_token;
                    }

                    // Pass the registration flow through so the backend stores
                    // registration_flow='consultation' for consultation signups.
                    if (this.flow === 'consultation' || this.flow === 'evaluation'){
                        data.flow = this.flow;
                    }

                    if (this.capRunning && this.isProduction){
                        AppAPI.Notifications.getDeviceToken(response=>{
                            const deviceToken = response.token;
                            data.deviceToken = deviceToken;
                            this.$emit('register', data);
                        }, error=>{
                            this.$emit('register', data);
                        })
                    }else{
                        this.$emit('register', data);
                    }
                }
            }, 250);
        },

        phoneCountryChanged(country){
            this.phoneCountry = country;
        },
        phoneValidated(data){
            this.phoneValid = data.isValid;
            this.fullPhone = data.number;
            if (data.country){
                this.phoneCountry = data.country;
            }
        },
        formValidate(){
            if (this.$refs.form.validate()){
                if (!this.phoneValid){
                    this.showMsg('Phone number is not valid');
                    return false;
                }
                return true;
            }else{
                this.showMsg('Data you entered are not valid');
            }
            return false;
        },

        showMsg(text, color = 'error', timeout = 8000){
            this.$store.dispatch('addSnackMsg', {show: true, text: text, color: color, timeout: timeout, msgBar: true});
        },
        ...GlobalMethods
    },
};
</script>

<style lang="scss" scoped>
.submit-btn {
    height: 52px !important;
    border-radius: 12px !important;
    font-family: 'Montserrat', sans-serif;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: none;
}
</style>
